import axios from "axios";
import { Shield, X, Loader2, CheckCircle2 } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { showError, showSuccess } from "../../../utils/toastUtils";

const OTP_LENGTH = 4;

const VerifyOTPModal = ({ isOpen, bookingId, onClose, onVerified }) => {
  const [otp, setOtp] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  // Single hidden input — the only real input
  const hiddenInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setClosing(false);
      setVisible(true);
      setOtp("");
    }
  }, [isOpen, bookingId]);

  useEffect(() => {
    if (cooldown <= 0) return;

    const timeout = setTimeout(() => {
      setCooldown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [cooldown]);

  // Focus hidden input when modal opens
  useEffect(() => {
    if (isOpen && visible) {
      setTimeout(() => hiddenInputRef.current?.focus(), 100);
    }
  }, [isOpen, visible]);

  const handleResendOtp = async () => {
    if (cooldown > 0 || resending) return;

    setResending(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/staff-assignment/generate-start-otp/${bookingId}`,
        { force: true }
      );

      showSuccess("OTP resent successfully");

      // start 30 sec cooldown
      setCooldown(30);

    } catch (error) {
      showError(error.response?.data?.message || "Failed to resend OTP");
    } finally {
      setResending(false);
    }
  };

  // Single input handler — dead simple
  const handleHiddenInput = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH);
    setOtp(val);
  };

  const handleVerify = async () => {
    if (otp.length !== OTP_LENGTH) {
      showError("Please enter complete 4-digit OTP");
      return;
    }
    setVerifying(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/staff-assignment/verify-otp`,
        { bookingId, otp }
      );
      if (response.data.success) {
        showSuccess("OTP verified successfully!");
        setTimeout(() => {
          onVerified();
          handleClose();
        }, 800); // slight delay for user to see success
      }
    } catch (error) {
      const message = error.response?.data?.message || "Invalid OTP";
      showError(message);
      setOtp("");
      hiddenInputRef.current?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      setClosing(false);
      setOtp("");
      onClose();
    }, 300);
  };

  if (!visible) return null;

  return (
    <>
      <div
        onClick={handleClose}
        className="fixed inset-0 bg-black/50 z-50"
        style={{
          animation: closing
            ? "fadeOut 0.3s ease forwards"
            : "fadeIn 0.3s ease forwards",
        }}
      />

      {/* Mobile: Bottom Sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl max-h-[85vh] overflow-hidden flex flex-col md:hidden"
        style={{
          animation: closing
            ? "slideDown 0.3s ease-in forwards"
            : "slideUp 0.3s ease-out forwards",
        }}
      >
        <div className="flex justify-center py-2 bg-gradient-to-r from-primaryColor via-brandAccentColor to-primaryColor">
          <div className="w-12 h-1.5 bg-white/80 rounded-full" />
        </div>
        <ModalContent
          otp={otp}
          hiddenInputRef={hiddenInputRef}
          handleHiddenInput={handleHiddenInput}
          isFocused={isFocused}
          setIsFocused={setIsFocused}
          handleVerify={handleVerify}
          handleClose={handleClose}
          handleResendOtp={handleResendOtp}
          verifying={verifying}
          cooldown={cooldown}
          resending={resending}
        />
      </div>

      {/* Desktop: Centered Modal */}
      <div
        className="hidden md:flex fixed inset-0 items-center justify-center z-50 p-4"
        onClick={(e) => e.target === e.currentTarget && handleClose()}
      >
        <div
          className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col"
          style={{
            animation: closing
              ? "scaleOut 0.3s ease forwards"
              : "scaleIn 0.3s ease forwards",
          }}
        >
          <ModalContent
            otp={otp}
            hiddenInputRef={hiddenInputRef}
            handleHiddenInput={handleHiddenInput}
            isFocused={isFocused}
            setIsFocused={setIsFocused}
            handleVerify={handleVerify}
            handleClose={handleClose}
            handleResendOtp={handleResendOtp}
            verifying={verifying}
            cooldown={cooldown}
            resending={resending}
          />
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fadeOut { from { opacity: 1; } to { opacity: 0; } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        @keyframes scaleOut { from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(0.95); } }
      `}</style>
    </>
  );
};

const ModalContent = ({
  otp,
  hiddenInputRef,
  handleHiddenInput,
  isFocused,
  setIsFocused,
  handleVerify,
  handleClose,
  verifying,
  handleResendOtp,
  cooldown,
  resending
}) => {
  const digits = otp.split("");

  return (
    <>
      {/* Header */}
      <div className="relative">
        <div className="h-32 md:h-34 bg-gradient-to-r from-primaryColor via-brandAccentColor to-primaryColor" />
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 w-9 h-9 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition shadow-lg active:scale-95"
        >
          <X size={18} className="text-gray-700" />
        </button>
        <div className="absolute -bottom-12 md:-bottom-14 left-1/2 -translate-x-1/2">
          <div className="w-24 h-24 md:w-28 md:h-28 rounded-full border-4 border-white shadow-xl bg-gradient-to-br from-primaryColor via-brandAccentColor to-primaryColor flex items-center justify-center">
            <Shield size={40} className="md:w-12 md:h-12 text-blue-600" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 md:px-6 pt-16 md:pt-18 pb-6">
        <div className="text-center mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
            Verify Customer OTP
          </h2>
          <p className="text-sm md:text-base text-gray-600">
            Ask customer for their 4-digit verification code
          </p>
        </div>

        {(
          <>

            <div
              className="relative flex justify-center gap-3 md:gap-4 mb-6"
              onClick={() => hiddenInputRef.current?.focus()}
            >
              {/* Hidden real input — positioned off screen but accessible */}
              <input
                ref={hiddenInputRef}
                type="text"
                inputMode="numeric"
                value={otp}
                onChange={handleHiddenInput}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                disabled={verifying}
                maxLength={OTP_LENGTH}
                style={{
                  position: "absolute",
                  opacity: 0,
                  width: "100%",
                  height: "100%",
                  top: 0,
                  left: 0,
                  cursor: "default",
                  zIndex: 1,
                }}
                autoComplete="one-time-code"
              />

              {/* Visual fake boxes — purely decorative */}
              {Array.from({ length: OTP_LENGTH }).map((_, i) => (
                <div
                  key={i}
                  className={`w-14 h-14 md:w-16 md:h-16 flex items-center justify-center text-2xl md:text-3xl font-bold border-2 rounded-xl transition-all
                    ${digits[i] ? "border-primaryColor bg-primaryColor/5" : "border-gray-300"}
                    ${isFocused && digits.length === i ? "border-primaryColor ring-2 ring-brandAccentColor" : ""}
                  `}
                >
                  {digits[i] || (
                    <span className={`w-2 h-2 rounded-full ${isFocused && digits.length === i ? "bg-primaryColor animate-pulse" : "bg-gray-300"}`} />
                  )}
                </div>
              ))}
            </div>
            <div className="text-center mb-4">
              <button
                onClick={handleResendOtp}
                disabled={cooldown > 0 || resending}
                className={`text-sm font-semibold transition active:scale-95 ${cooldown > 0 || resending
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-primaryBtnColor hover:underline active:opacity-70"
                  }`}
              >
                {resending ? (
                  <span className="flex items-center justify-center gap-1">
                    <Loader2 size={14} className="animate-spin" />
                    Sending...
                  </span>
                ) : cooldown > 0 ? (
                  <span>
                    Resend in <span className="font-bold">{cooldown}s</span>
                  </span>
                ) : (
                  "Resend OTP"
                )}
              </button>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 md:p-4 mb-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">📋 Instructions:</h4>
              <ul className="text-xs md:text-sm text-gray-600 space-y-1.5">
                <li>• Ask customer for their 4-digit OTP</li>
              </ul>
            </div>

            <button
              onClick={handleVerify}
              disabled={verifying || otp.length !== OTP_LENGTH}
              className="w-full py-3 md:py-3.5 bg-primaryBtnColor text-white font-bold rounded-xl hover:bg-primaryBtnHoverColor active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm md:text-base"
            >
              {verifying ? (
                <><Loader2 size={20} className="animate-spin" />Verifying...</>
              ) : (
                <><Shield size={20} />Verify OTP</>
              )}
            </button>

          </>
        )}

        <button
          onClick={handleClose}
          className="w-full mt-4 py-2.5 md:py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 active:scale-[0.98] transition text-sm md:text-base"
        >
          Cancel
        </button>
      </div>
    </>
  );
};

export default VerifyOTPModal;